import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import MainLayout from '../../components/layout/MainLayout';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { getTenderById, getGapItems, updateGapItemStatus, getTenderTasks } from '../../services/tenderService';
import { useChat } from '../../hooks/useChat';
import toast from 'react-hot-toast';

const GAP_STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'bg-warning/15 text-warning' },
  in_progress: { label: 'In Progress', color: 'bg-blue-500/15 text-blue-400' },
  completed: { label: 'Completed', color: 'bg-success/15 text-success' },
};

const ComplianceGauge = ({ value, label, size = 'md' }) => {
  const radius = size === 'lg' ? 54 : 36;
  const stroke = size === 'lg' ? 8 : 6;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (value / 100) * circumference;
  const color = value >= 90 ? '#22c55e' : value >= 75 ? '#f59e0b' : '#ef4444';
  const svgSize = (radius + stroke) * 2 + 4;

  return (
    <div className="flex flex-col items-center gap-1">
      <svg width={svgSize} height={svgSize} className="-rotate-90">
        <circle cx={svgSize / 2} cy={svgSize / 2} r={radius} fill="none" stroke="currentColor" strokeWidth={stroke} className="text-muted/40" />
        <circle cx={svgSize / 2} cy={svgSize / 2} r={radius} fill="none" stroke={color} strokeWidth={stroke}
          strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 1s ease' }} />
      </svg>
      <div className="text-center -mt-1">
        <div className={`font-bold ${size === 'lg' ? 'text-3xl' : 'text-lg'}`} style={{ color }}>{value}%</div>
        <div className="text-xs text-muted-foreground">{label}</div>
      </div>
    </div>
  );
};

const InfoRow = ({ label, value }) => {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2 py-1.5 border-b border-border/50 last:border-0">
      <span className="text-xs text-muted-foreground w-36 flex-shrink-0 pt-0.5">{label}</span>
      <span className="text-sm text-foreground font-medium">{value}</span>
    </div>
  );
};

const SectionList = ({ title, icon, items, color = 'text-primary' }) => {
  if (!items?.length) return null;
  return (
    <div>
      <h4 className={`font-semibold text-sm mb-2 flex items-center gap-1.5 ${color}`}>
        <Icon name={icon} size={15} />
        {title}
      </h4>
      <ul className="space-y-1">
        {items?.map((item, i) => (
          <li key={i} className="text-sm text-foreground flex items-start gap-2">
            <span className="text-muted-foreground mt-0.5 flex-shrink-0">•</span>
            <span>{typeof item === 'object' ? (item?.role ? `${item.role} ×${item.count || 1} (${item.experience || ''})` : JSON.stringify(item)) : item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const ComplianceReports = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tender, setTender] = useState(null);
  const [gapItems, setGapItems] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const chatEndRef = useRef(null);

  const { response, isLoading: chatLoading, error: chatError, sendMessage } = useChat('OPEN_AI', 'gpt-4.1', true);

  useEffect(() => {
    if (chatError) toast.error(chatError.message);
  }, [chatError]);

  useEffect(() => {
    if (response && !chatLoading) {
      setChatMessages((prev) => {
        const last = prev[prev.length - 1];
        if (last?.role === 'assistant' && last?.streaming) {
          return [...prev.slice(0, -1), { role: 'assistant', content: response }];
        }
        return prev;
      });
    }
  }, [response, chatLoading]);

  useEffect(() => {
    if (chatLoading && response) {
      setChatMessages((prev) => {
        const last = prev[prev.length - 1];
        if (last?.role === 'assistant') {
          return [...prev.slice(0, -1), { role: 'assistant', content: response, streaming: true }];
        }
        return [...prev, { role: 'assistant', content: response, streaming: true }];
      });
    }
  }, [response, chatLoading]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  useEffect(() => {
    if (id) loadData();
  }, [id]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [t, gaps, taskList] = await Promise.all([
        getTenderById(id),
        getGapItems(id),
        getTenderTasks(id),
      ]);
      setTender(t);
      setGapItems(gaps);
      setTasks(taskList);
    } catch {
      toast.error('Failed to load compliance report');
    } finally {
      setLoading(false);
    }
  };

  const handleGapStatusChange = async (gapId, newStatus) => {
    try {
      await updateGapItemStatus(gapId, newStatus);
      setGapItems((prev) => prev.map((g) => g.id === gapId ? { ...g, gapStatus: newStatus } : g));
      toast.success('Status updated');
    } catch {
      toast.error('Failed to update status');
    }
  };

  const handleChatSend = () => {
    if (!chatInput.trim() || chatLoading) return;
    const userMsg = chatInput.trim();
    setChatInput('');
    const newHistory = [...chatMessages, { role: 'user', content: userMsg }];
    setChatMessages(newHistory);

    const analysis = tender?.analysisResult || {};
    const aiAnalysis = analysis?.aiAnalysis || {};
    const gapAnalysis = analysis?.gapAnalysis || {};
    const recommendations = analysis?.recommendations || {};

    const systemContext = `You are an AI assistant helping analyze a tender document.
Tender: ${tender?.tenderName}
Client: ${tender?.clientName}
Compliance: ${tender?.complianceOverall}%
Gap Items: ${gapItems.map((g) => g.itemName).join(', ')}
Key Risks: ${(aiAnalysis?.keyRisks || []).join(', ')}
Major Requirements: ${(aiAnalysis?.majorRequirements || []).join(', ')}
Missing Engineers: ${(gapAnalysis?.missingEngineers || []).map((e) => e?.name).join(', ')}
Missing Documents: ${(gapAnalysis?.missingDocuments || []).map((d) => d?.name).join(', ')}
Recommendations: ${recommendations?.recommendation || ''}
Readiness: ${recommendations?.readinessScore || ''}
Answer questions about this specific tender concisely and professionally.`;

    sendMessage([
      { role: 'system', content: systemContext },
      ...newHistory.map((m) => ({ role: m.role, content: m.content })),
    ], { max_tokens: 800 });
  };

  const handleExport = (type) => {
    if (type === 'pdf') {
      window.print();
      toast.success('Opening print dialog for PDF export');
    } else {
      toast.success(`${type.toUpperCase()} export initiated`);
    }
  };

  const analysis = tender?.analysisResult || {};
  const basicInfo = analysis?.basicInfo || {};
  const projectInfo = analysis?.projectInfo || {};
  const techReq = analysis?.technicalRequirements || {};
  const commReq = analysis?.commercialRequirements || {};
  const legalReq = analysis?.legalRequirements || {};
  const hseReq = analysis?.hseRequirements || {};
  const aiAnalysis = analysis?.aiAnalysis || {};
  const gapAnalysis = analysis?.gapAnalysis || {};
  const recommendations = analysis?.recommendations || {};
  const requiredDocs = analysis?.requiredDocuments || [];

  const gapByCategory = gapItems.reduce((acc, g) => {
    if (!acc[g.category]) acc[g.category] = [];
    acc[g.category].push(g);
    return acc;
  }, {});

  const totalGapCount = (gapAnalysis?.missingEngineers?.length || 0) +
    (gapAnalysis?.missingDocuments?.length || 0) +
    (gapAnalysis?.missingCertificates?.length || 0) +
    (gapAnalysis?.missingEquipment?.length || 0) +
    (gapAnalysis?.missingExperience?.length || 0) +
    (gapAnalysis?.missingFinancialDocs?.length || 0) +
    (gapAnalysis?.missingLicenses?.length || 0);

  const TABS = [
    { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
    { id: 'basic', label: 'Basic Info', icon: 'Info' },
    { id: 'project', label: 'Project Info', icon: 'FolderOpen' },
    { id: 'technical', label: 'Technical', icon: 'Settings' },
    { id: 'commercial', label: 'Commercial', icon: 'DollarSign' },
    { id: 'legal', label: 'Legal', icon: 'Scale' },
    { id: 'hse', label: 'HSE', icon: 'ShieldAlert' },
    { id: 'documents', label: 'Documents', icon: 'FileCheck' },
    { id: 'requirements', label: 'Requirements', icon: 'ClipboardList' },
    { id: 'gaps', label: `Gap Analysis (${gapItems.length || totalGapCount})`, icon: 'AlertTriangle' },
    { id: 'recommendations', label: 'Recommendations', icon: 'Lightbulb' },
    { id: 'summary', label: 'Executive Summary', icon: 'FileText' },
  ];

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <Icon name="Loader2" size={32} className="animate-spin text-primary" />
        </div>
      </MainLayout>
    );
  }

  if (!tender) {
    return (
      <MainLayout>
        <div className="text-center py-16">
          <Icon name="FileX" size={40} className="mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground">Tender not found</p>
          <Button variant="outline" onClick={() => navigate('/tender-uploaded')} className="mt-3">Back to Tenders</Button>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-5">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <button onClick={() => navigate('/tender-uploaded')} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-2">
              <Icon name="ChevronLeft" size={16} />
              Back to Tenders
            </button>
            <h1 className="text-xl font-bold text-foreground">{tender?.tenderName}</h1>
            <p className="text-sm text-muted-foreground">{tender?.clientName} • {tender?.tenderNumber}</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => setChatOpen(true)} className="gap-2" variant="outline">
              <Icon name="MessageSquare" size={16} />
              AI Assistant
            </Button>
            <div className="relative group">
              <Button variant="outline" className="gap-2">
                <Icon name="Download" size={16} />
                Export
              </Button>
              <div className="absolute right-0 top-full mt-1 bg-card border border-border rounded-xl shadow-lg p-1 z-10 hidden group-hover:block min-w-[130px]">
                {['PDF', 'Excel', 'Word'].map((fmt) => (
                  <button key={fmt} onClick={() => handleExport(fmt.toLowerCase())}
                    className="w-full text-left text-sm px-3 py-1.5 hover:bg-muted/50 rounded-lg text-foreground flex items-center gap-2">
                    <Icon name={fmt === 'PDF' ? 'FileText' : fmt === 'Excel' ? 'Table' : 'FileType'} size={13} />
                    {fmt}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-muted/30 rounded-xl p-1 overflow-x-auto">
          {TABS.map((tab) => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap
                ${activeTab === tab.id ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>
              <Icon name={tab.icon} size={13} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* ── OVERVIEW TAB ── */}
        {activeTab === 'overview' && (
          <div className="space-y-5">
            {/* Compliance Gauges */}
            <div className="bg-card border border-border rounded-2xl p-6">
              <h3 className="font-semibold text-foreground mb-5 flex items-center gap-2">
                <Icon name="ShieldCheck" size={18} className="text-primary" />
                Compliance Scores
              </h3>
              <div className="flex flex-wrap justify-around gap-6">
                <ComplianceGauge value={tender?.complianceOverall || 0} label="Overall" size="lg" />
                <ComplianceGauge value={tender?.complianceTechnical || 0} label="Technical" />
                <ComplianceGauge value={tender?.complianceCommercial || 0} label="Commercial" />
                <ComplianceGauge value={tender?.complianceLegal || 0} label="Legal" />
                <ComplianceGauge value={tender?.complianceResources || 0} label="Resources" />
              </div>
            </div>

            {/* KPI Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: 'Gap Items', value: gapItems.length || totalGapCount, icon: 'AlertTriangle', color: 'text-warning' },
                { label: 'Tasks Generated', value: tasks.length, icon: 'ListTodo', color: 'text-primary' },
                { label: 'Gaps Resolved', value: gapItems.filter((g) => g.gapStatus === 'completed').length, icon: 'CheckCircle2', color: 'text-success' },
                { label: 'Pending Tasks', value: tasks.filter((t) => t.taskStatus === 'pending').length, icon: 'Clock', color: 'text-muted-foreground' },
              ].map((stat) => (
                <div key={stat.label} className="bg-card border border-border rounded-xl p-4">
                  <Icon name={stat.icon} size={20} className={`${stat.color} mb-2`} />
                  <div className="text-2xl font-bold text-foreground">{stat.value}</div>
                  <div className="text-xs text-muted-foreground">{stat.label}</div>
                </div>
              ))}
            </div>

            {/* AI Assessment */}
            {(recommendations?.readinessScore || recommendations?.probabilityOfSuccess) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-card border border-border rounded-2xl p-5">
                  <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                    <Icon name="Brain" size={18} className="text-primary" />
                    AI Assessment
                  </h3>
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div className="text-center bg-success/10 rounded-xl p-3">
                      <div className="text-2xl font-bold text-success">{recommendations?.readinessScore || '—'}</div>
                      <div className="text-xs text-muted-foreground">Readiness Score</div>
                    </div>
                    <div className="text-center bg-primary/10 rounded-xl p-3">
                      <div className="text-2xl font-bold text-primary">{recommendations?.probabilityOfSuccess || '—'}</div>
                      <div className="text-xs text-muted-foreground">Success Probability</div>
                    </div>
                  </div>
                  {recommendations?.recommendation && (
                    <div className="bg-primary/5 border border-primary/20 rounded-xl p-3">
                      <p className="text-xs font-semibold text-primary mb-1">Recommendation</p>
                      <p className="text-sm text-foreground">{recommendations?.recommendation}</p>
                    </div>
                  )}
                </div>

                <div className="bg-card border border-border rounded-2xl p-5">
                  <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                    <Icon name="Info" size={18} className="text-primary" />
                    Tender Overview
                  </h3>
                  <div className="space-y-0.5">
                    <InfoRow label="Tender Type" value={basicInfo?.tenderType} />
                    <InfoRow label="Contract Duration" value={basicInfo?.contractDuration || aiAnalysis?.projectDuration} />
                    <InfoRow label="Bid Validity" value={basicInfo?.bidValidity} />
                    <InfoRow label="Bid Bond" value={commReq?.bidBond} />
                    <InfoRow label="Performance Bond" value={commReq?.performanceBond} />
                    <InfoRow label="Payment Terms" value={commReq?.paymentTerms} />
                  </div>
                </div>
              </div>
            )}

            {/* Risks */}
            {aiAnalysis?.keyRisks?.length > 0 && (
              <div className="bg-card border border-border rounded-2xl p-5">
                <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                  <Icon name="AlertOctagon" size={18} className="text-error" />
                  Key Risks
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {aiAnalysis?.keyRisks?.map((risk, i) => (
                    <div key={i} className="flex items-start gap-2 bg-error/5 border border-error/20 rounded-lg px-3 py-2">
                      <Icon name="AlertCircle" size={14} className="text-error mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-foreground">{risk}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Fallback: old risks field */}
            {!aiAnalysis?.keyRisks?.length && analysis?.risks?.length > 0 && (
              <div className="bg-card border border-border rounded-2xl p-5">
                <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                  <Icon name="AlertOctagon" size={18} className="text-error" />
                  Identified Risks
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {analysis?.risks?.map((risk, i) => (
                    <div key={i} className="flex items-start gap-2 bg-error/5 border border-error/20 rounded-lg px-3 py-2">
                      <Icon name="AlertCircle" size={14} className="text-error mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-foreground">{risk}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── BASIC INFO TAB ── */}
        {activeTab === 'basic' && (
          <div className="bg-card border border-border rounded-2xl p-6">
            <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="Info" size={18} className="text-primary" />
              Basic Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
              <div>
                <InfoRow label="Tender Name" value={basicInfo?.tenderName || tender?.tenderName} />
                <InfoRow label="Tender Number" value={basicInfo?.tenderNumber || tender?.tenderNumber} />
                <InfoRow label="Client Name" value={basicInfo?.clientName || tender?.clientName} />
                <InfoRow label="Project Name" value={basicInfo?.projectName || tender?.projectName} />
                <InfoRow label="Tender Type" value={basicInfo?.tenderType} />
                <InfoRow label="Tender Category" value={basicInfo?.tenderCategory || tender?.tenderCategory} />
              </div>
              <div>
                <InfoRow label="Country" value={basicInfo?.country} />
                <InfoRow label="City" value={basicInfo?.city} />
                <InfoRow label="Issue Date" value={basicInfo?.issueDate} />
                <InfoRow label="Closing Date" value={basicInfo?.closingDate || tender?.closingDate} />
                <InfoRow label="Submission Date" value={basicInfo?.submissionDate || tender?.submissionDate} />
                <InfoRow label="Tender Value" value={basicInfo?.tenderValue} />
                <InfoRow label="Contract Duration" value={basicInfo?.contractDuration} />
                <InfoRow label="Bid Validity" value={basicInfo?.bidValidity} />
              </div>
            </div>
          </div>
        )}

        {/* ── PROJECT INFO TAB ── */}
        {activeTab === 'project' && (
          <div className="space-y-4">
            {projectInfo?.projectDescription && (
              <div className="bg-card border border-border rounded-2xl p-5">
                <h3 className="font-semibold text-foreground mb-2 flex items-center gap-2">
                  <Icon name="FileText" size={16} className="text-primary" />
                  Project Description
                </h3>
                <p className="text-sm text-foreground leading-relaxed">{projectInfo?.projectDescription}</p>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-card border border-border rounded-2xl p-4">
                <SectionList title="Scope of Work" icon="Target" items={projectInfo?.scopeOfWork || analysis?.scopeOfWork} />
              </div>
              <div className="bg-card border border-border rounded-2xl p-4">
                <SectionList title="Deliverables" icon="Package" items={projectInfo?.deliverables} color="text-blue-400" />
              </div>
              <div className="bg-card border border-border rounded-2xl p-4">
                <SectionList title="Technical Scope" icon="Settings" items={projectInfo?.technicalScope} color="text-purple-400" />
              </div>
              <div className="bg-card border border-border rounded-2xl p-4">
                <SectionList title="Commercial Scope" icon="DollarSign" items={projectInfo?.commercialScope} color="text-success" />
              </div>
              <div className="bg-card border border-border rounded-2xl p-4">
                <SectionList title="Project Objectives" icon="Flag" items={projectInfo?.projectObjectives} color="text-warning" />
              </div>
              {projectInfo?.expectedTimeline && (
                <div className="bg-card border border-border rounded-2xl p-4">
                  <h4 className="font-semibold text-sm mb-2 flex items-center gap-1.5 text-teal-400">
                    <Icon name="Clock" size={15} />
                    Expected Timeline
                  </h4>
                  <p className="text-sm text-foreground">{projectInfo?.expectedTimeline}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── TECHNICAL TAB ── */}
        {activeTab === 'technical' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-card border border-border rounded-2xl p-4">
              <SectionList title="Required Engineers" icon="Users" items={techReq?.requiredEngineers || analysis?.requiredEngineers} />
            </div>
            <div className="bg-card border border-border rounded-2xl p-4">
              <SectionList title="Required Certifications" icon="Award" items={techReq?.requiredCertifications || analysis?.requiredCertifications} color="text-success" />
            </div>
            <div className="bg-card border border-border rounded-2xl p-4">
              <SectionList title="Required Equipment" icon="Wrench" items={techReq?.requiredEquipment || analysis?.requiredEquipment} color="text-orange-400" />
            </div>
            <div className="bg-card border border-border rounded-2xl p-4">
              <SectionList title="Required Vehicles" icon="Truck" items={techReq?.requiredVehicles} color="text-blue-400" />
            </div>
            <div className="bg-card border border-border rounded-2xl p-4">
              <SectionList title="Required Software" icon="Monitor" items={techReq?.requiredSoftware} color="text-purple-400" />
            </div>
            <div className="bg-card border border-border rounded-2xl p-4">
              <SectionList title="Required Experience" icon="Briefcase" items={techReq?.requiredExperience || analysis?.requiredExperience} color="text-teal-400" />
            </div>
            <div className="bg-card border border-border rounded-2xl p-4">
              <SectionList title="Required Standards" icon="BookOpen" items={techReq?.requiredStandards} color="text-indigo-400" />
            </div>
            <div className="bg-card border border-border rounded-2xl p-4">
              <SectionList title="Required Methodology" icon="GitBranch" items={techReq?.requiredMethodology} color="text-pink-400" />
            </div>
            <div className="bg-card border border-border rounded-2xl p-4 md:col-span-2">
              <SectionList title="Required Materials" icon="Package" items={techReq?.requiredMaterials} color="text-amber-400" />
            </div>
          </div>
        )}

        {/* ── COMMERCIAL TAB ── */}
        {activeTab === 'commercial' && (
          <div className="bg-card border border-border rounded-2xl p-6">
            <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="DollarSign" size={18} className="text-primary" />
              Commercial Requirements
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
              <div>
                <InfoRow label="Bid Bond" value={commReq?.bidBond} />
                <InfoRow label="Performance Bond" value={commReq?.performanceBond} />
                <InfoRow label="Bank Guarantee" value={commReq?.bankGuarantee} />
                <InfoRow label="Payment Terms" value={commReq?.paymentTerms} />
                <InfoRow label="Commercial Proposal" value={commReq?.commercialProposal} />
              </div>
              <div className="space-y-3">
                <SectionList title="Financial Requirements" icon="Banknote" items={commReq?.financialRequirements || analysis?.financialRequirements} color="text-success" />
                <SectionList title="Tax Requirements" icon="Receipt" items={commReq?.taxRequirements} color="text-warning" />
                <SectionList title="Pricing Instructions" icon="Tag" items={commReq?.pricingInstructions} color="text-blue-400" />
              </div>
            </div>
          </div>
        )}

        {/* ── LEGAL TAB ── */}
        {activeTab === 'legal' && (
          <div className="bg-card border border-border rounded-2xl p-6">
            <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="Scale" size={18} className="text-primary" />
              Legal Requirements
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
              <div>
                <InfoRow label="Company Registration" value={legalReq?.companyRegistration} />
                <InfoRow label="Chamber Certificate" value={legalReq?.chamberCertificate} />
                <InfoRow label="VAT Certificate" value={legalReq?.vatCertificate} />
                <InfoRow label="NDA" value={legalReq?.nda} />
              </div>
              <div className="space-y-3">
                <SectionList title="Required Licenses" icon="FileKey" items={legalReq?.licenses || analysis?.legalRequirements} color="text-purple-400" />
                <SectionList title="ISO Certificates" icon="Award" items={legalReq?.isoCertificates} color="text-success" />
                <SectionList title="Contract Conditions" icon="FileSignature" items={legalReq?.contractConditions || analysis?.contractConditions} color="text-warning" />
                <SectionList title="Compliance Requirements" icon="CheckSquare" items={legalReq?.complianceRequirements} color="text-blue-400" />
              </div>
            </div>
          </div>
        )}

        {/* ── HSE TAB ── */}
        {activeTab === 'hse' && (
          <div className="bg-card border border-border rounded-2xl p-6">
            <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="ShieldAlert" size={18} className="text-primary" />
              HSE Requirements
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
              <div>
                <InfoRow label="HSE Plan" value={hseReq?.hsePlan} />
                <InfoRow label="Risk Assessment" value={hseReq?.riskAssessment} />
              </div>
              <div className="space-y-3">
                <SectionList title="Safety Certificates" icon="Award" items={hseReq?.safetyCertificates} color="text-success" />
                <SectionList title="PPE Requirements" icon="HardHat" items={hseReq?.ppeRequirements} color="text-warning" />
                <SectionList title="Environmental Requirements" icon="Leaf" items={hseReq?.environmentalRequirements} color="text-teal-400" />
              </div>
            </div>
            {(!hseReq?.hsePlan && !hseReq?.safetyCertificates?.length) && (
              <div className="text-center py-8 text-muted-foreground">
                <Icon name="ShieldAlert" size={32} className="mx-auto mb-2 opacity-40" />
                <p className="text-sm">HSE requirements will be extracted from uploaded documents</p>
              </div>
            )}
          </div>
        )}

        {/* ── DOCUMENTS TAB ── */}
        {activeTab === 'documents' && (
          <div className="bg-card border border-border rounded-2xl p-5">
            <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="FileCheck" size={18} className="text-primary" />
              Required Documents Checklist
            </h3>
            {requiredDocs?.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {requiredDocs?.map((doc, i) => {
                  const docName = typeof doc === 'string' ? doc : doc?.name;
                  const required = typeof doc === 'string' ? true : doc?.required !== false;
                  const notes = typeof doc === 'object' ? doc?.notes : '';
                  return (
                    <div key={i} className={`flex items-start gap-3 rounded-xl px-4 py-3 border ${required ? 'bg-muted/30 border-border' : 'bg-muted/10 border-border/50'}`}>
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${required ? 'border-primary' : 'border-muted-foreground/40'}`}>
                        {required && <Icon name="Check" size={11} className="text-primary" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium ${required ? 'text-foreground' : 'text-muted-foreground'}`}>{docName}</p>
                        {notes && <p className="text-xs text-muted-foreground mt-0.5">{notes}</p>}
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${required ? 'bg-error/10 text-error' : 'bg-muted text-muted-foreground'}`}>
                        {required ? 'Required' : 'Optional'}
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {['Company Profile', 'Commercial Registration', 'VAT Certificate', 'ISO Certificates', 'Financial Statements', 'Experience Certificates', 'Similar Projects', 'Engineer CVs', 'Equipment List', 'Organization Chart', 'Method Statement', 'HSE Plan', 'Bank Guarantee', 'Tender Forms', 'BOQ', 'Technical Proposal', 'Commercial Proposal'].map((doc, i) => (
                  <div key={i} className="flex items-center gap-3 rounded-xl px-4 py-3 border bg-muted/30 border-border">
                    <div className="w-5 h-5 rounded border-2 border-primary flex items-center justify-center flex-shrink-0">
                      <Icon name="Check" size={11} className="text-primary" />
                    </div>
                    <p className="text-sm font-medium text-foreground">{doc}</p>
                    <span className="ml-auto text-xs px-2 py-0.5 rounded-full bg-error/10 text-error">Required</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── REQUIREMENTS TAB ── */}
        {activeTab === 'requirements' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { key: 'scopeOfWork', title: 'Scope of Work', icon: 'Target', color: 'text-primary', data: projectInfo?.scopeOfWork || analysis?.scopeOfWork },
              { key: 'technicalSpecifications', title: 'Technical Specifications', icon: 'Settings', color: 'text-blue-400', data: analysis?.technicalSpecifications },
              { key: 'commercialRequirements', title: 'Commercial Requirements', icon: 'DollarSign', color: 'text-purple-400', data: analysis?.commercialRequirements },
              { key: 'mandatoryDocuments', title: 'Mandatory Documents', icon: 'FileCheck', color: 'text-warning', data: analysis?.mandatoryDocuments },
              { key: 'requiredCertifications', title: 'Required Certifications', icon: 'Award', color: 'text-success', data: techReq?.requiredCertifications || analysis?.requiredCertifications },
              { key: 'requiredEngineers', title: 'Required Engineers', icon: 'Users', color: 'text-teal-400', data: techReq?.requiredEngineers || analysis?.requiredEngineers },
              { key: 'requiredEquipment', title: 'Required Equipment', icon: 'Wrench', color: 'text-orange-400', data: techReq?.requiredEquipment || analysis?.requiredEquipment },
              { key: 'financialRequirements', title: 'Financial Requirements', icon: 'Banknote', color: 'text-emerald-400', data: commReq?.financialRequirements || analysis?.financialRequirements },
              { key: 'legalRequirements', title: 'Legal Requirements', icon: 'Scale', color: 'text-indigo-400', data: legalReq?.licenses || analysis?.legalRequirements },
              { key: 'hseRequirements', title: 'HSE Requirements', icon: 'ShieldAlert', color: 'text-red-400', data: hseReq?.ppeRequirements || analysis?.hseRequirements },
              { key: 'evaluationCriteria', title: 'Evaluation Criteria', icon: 'Star', color: 'text-yellow-400', data: aiAnalysis?.evaluationCriteria || analysis?.evaluationCriteria },
              { key: 'contractConditions', title: 'Contract Conditions', icon: 'FileSignature', color: 'text-pink-400', data: legalReq?.contractConditions || analysis?.contractConditions },
            ].map(({ key, title, icon, color, data }) => {
              if (!data?.length) return null;
              return (
                <div key={key} className="bg-card border border-border rounded-2xl p-4">
                  <h4 className={`font-semibold text-sm mb-3 flex items-center gap-1.5 ${color}`}>
                    <Icon name={icon} size={15} />
                    {title}
                  </h4>
                  <ul className="space-y-1.5">
                    {data?.map((item, i) => (
                      <li key={i} className="text-sm text-foreground flex items-start gap-2">
                        <span className="text-muted-foreground mt-0.5 flex-shrink-0">•</span>
                        <span>{typeof item === 'object' ? (item?.role ? `${item.role} ×${item.count || 1} (${item.experience || ''})` : JSON.stringify(item)) : item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        )}

        {/* ── GAP ANALYSIS TAB ── */}
        {activeTab === 'gaps' && (
          <div className="space-y-4">
            {/* Gap Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { label: 'Missing Engineers', count: gapAnalysis?.missingEngineers?.length || gapItems.filter((g) => g.category === 'Engineers').length, icon: 'Users', color: 'text-error' },
                { label: 'Missing Documents', count: gapAnalysis?.missingDocuments?.length || gapItems.filter((g) => g.category === 'Documents').length, icon: 'FileX', color: 'text-warning' },
                { label: 'Missing Certificates', count: gapAnalysis?.missingCertificates?.length || gapItems.filter((g) => g.category === 'Certificates').length, icon: 'Award', color: 'text-orange-400' },
                { label: 'Missing Equipment', count: gapAnalysis?.missingEquipment?.length || gapItems.filter((g) => g.category === 'Equipment').length, icon: 'Wrench', color: 'text-blue-400' },
              ].map((stat) => (
                <div key={stat.label} className="bg-card border border-border rounded-xl p-3 text-center">
                  <Icon name={stat.icon} size={20} className={`${stat.color} mx-auto mb-1`} />
                  <div className={`text-2xl font-bold ${stat.color}`}>{stat.count}</div>
                  <div className="text-xs text-muted-foreground">{stat.label}</div>
                </div>
              ))}
            </div>

            {/* Gap Items from DB (with status management) */}
            {Object.keys(gapByCategory).length > 0 && (
              <div className="space-y-4">
                <h3 className="font-semibold text-foreground flex items-center gap-2">
                  <Icon name="Database" size={16} className="text-primary" />
                  Tracked Gap Items
                </h3>
                {Object.entries(gapByCategory).map(([category, items]) => (
                  <div key={category} className="bg-card border border-border rounded-2xl p-5">
                    <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                      <Icon name="AlertTriangle" size={16} className="text-warning" />
                      {category} ({items.length})
                    </h3>
                    <div className="space-y-2">
                      {items.map((gap) => (
                        <div key={gap.id} className="flex items-center justify-between bg-muted/30 rounded-xl px-4 py-3">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm text-foreground">{gap.itemName}</p>
                            <p className="text-xs text-muted-foreground mt-0.5">{gap.description}</p>
                          </div>
                          <select
                            value={gap.gapStatus}
                            onChange={(e) => handleGapStatusChange(gap.id, e.target.value)}
                            className={`ml-3 text-xs px-2 py-1 rounded-lg border-0 font-medium cursor-pointer focus:outline-none focus:ring-1 focus:ring-primary ${GAP_STATUS_CONFIG[gap.gapStatus]?.color}`}
                          >
                            {Object.entries(GAP_STATUS_CONFIG).map(([k, v]) => (
                              <option key={k} value={k}>{v.label}</option>
                            ))}
                          </select>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Detailed Gap Analysis from AI */}
            {totalGapCount > 0 && (
              <div className="space-y-4">
                <h3 className="font-semibold text-foreground flex items-center gap-2">
                  <Icon name="Brain" size={16} className="text-primary" />
                  AI Detailed Gap Analysis
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {gapAnalysis?.missingEngineers?.length > 0 && (
                    <div className="bg-card border border-border rounded-2xl p-4">
                      <h4 className="font-semibold text-sm mb-3 flex items-center gap-1.5 text-error">
                        <Icon name="Users" size={15} />
                        Missing Engineers ({gapAnalysis?.missingEngineers?.length})
                      </h4>
                      <div className="space-y-2">
                        {gapAnalysis?.missingEngineers?.map((item, i) => (
                          <div key={i} className="flex items-start justify-between gap-2 bg-muted/30 rounded-lg px-3 py-2">
                            <div>
                              <p className="text-sm font-medium text-foreground">{item?.name}</p>
                              {item?.notes && <p className="text-xs text-muted-foreground">{item?.notes}</p>}
                            </div>
                            <div className="flex gap-1.5 flex-shrink-0">
                              {item?.department && <span className="text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded-full">{item?.department}</span>}
                              <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${item?.priority === 'high' ? 'bg-error/15 text-error' : item?.priority === 'medium' ? 'bg-warning/15 text-warning' : 'bg-muted text-muted-foreground'}`}>{item?.priority}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {gapAnalysis?.missingDocuments?.length > 0 && (
                    <div className="bg-card border border-border rounded-2xl p-4">
                      <h4 className="font-semibold text-sm mb-3 flex items-center gap-1.5 text-warning">
                        <Icon name="FileX" size={15} />
                        Missing Documents ({gapAnalysis?.missingDocuments?.length})
                      </h4>
                      <div className="space-y-2">
                        {gapAnalysis?.missingDocuments?.map((item, i) => (
                          <div key={i} className="flex items-start justify-between gap-2 bg-muted/30 rounded-lg px-3 py-2">
                            <div>
                              <p className="text-sm font-medium text-foreground">{item?.name}</p>
                              {item?.notes && <p className="text-xs text-muted-foreground">{item?.notes}</p>}
                            </div>
                            <div className="flex gap-1.5 flex-shrink-0">
                              {item?.department && <span className="text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded-full">{item?.department}</span>}
                              <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${item?.priority === 'high' ? 'bg-error/15 text-error' : item?.priority === 'medium' ? 'bg-warning/15 text-warning' : 'bg-muted text-muted-foreground'}`}>{item?.priority}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {gapAnalysis?.missingCertificates?.length > 0 && (
                    <div className="bg-card border border-border rounded-2xl p-4">
                      <h4 className="font-semibold text-sm mb-3 flex items-center gap-1.5 text-orange-400">
                        <Icon name="Award" size={15} />
                        Missing Certificates ({gapAnalysis?.missingCertificates?.length})
                      </h4>
                      <div className="space-y-2">
                        {gapAnalysis?.missingCertificates?.map((item, i) => (
                          <div key={i} className="flex items-start justify-between gap-2 bg-muted/30 rounded-lg px-3 py-2">
                            <div>
                              <p className="text-sm font-medium text-foreground">{item?.name}</p>
                              {item?.notes && <p className="text-xs text-muted-foreground">{item?.notes}</p>}
                            </div>
                            <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium flex-shrink-0 ${item?.priority === 'high' ? 'bg-error/15 text-error' : item?.priority === 'medium' ? 'bg-warning/15 text-warning' : 'bg-muted text-muted-foreground'}`}>{item?.priority}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {gapAnalysis?.missingEquipment?.length > 0 && (
                    <div className="bg-card border border-border rounded-2xl p-4">
                      <h4 className="font-semibold text-sm mb-3 flex items-center gap-1.5 text-blue-400">
                        <Icon name="Wrench" size={15} />
                        Missing Equipment ({gapAnalysis?.missingEquipment?.length})
                      </h4>
                      <div className="space-y-2">
                        {gapAnalysis?.missingEquipment?.map((item, i) => (
                          <div key={i} className="flex items-start justify-between gap-2 bg-muted/30 rounded-lg px-3 py-2">
                            <div>
                              <p className="text-sm font-medium text-foreground">{item?.name}</p>
                              {item?.notes && <p className="text-xs text-muted-foreground">{item?.notes}</p>}
                            </div>
                            <div className="flex gap-1.5 flex-shrink-0">
                              {item?.department && <span className="text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded-full">{item?.department}</span>}
                              <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${item?.priority === 'high' ? 'bg-error/15 text-error' : item?.priority === 'medium' ? 'bg-warning/15 text-warning' : 'bg-muted text-muted-foreground'}`}>{item?.priority}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {gapAnalysis?.missingFinancialDocs?.length > 0 && (
                    <div className="bg-card border border-border rounded-2xl p-4">
                      <h4 className="font-semibold text-sm mb-3 flex items-center gap-1.5 text-success">
                        <Icon name="Banknote" size={15} />
                        Missing Financial Docs ({gapAnalysis?.missingFinancialDocs?.length})
                      </h4>
                      <div className="space-y-2">
                        {gapAnalysis?.missingFinancialDocs?.map((item, i) => (
                          <div key={i} className="flex items-start justify-between gap-2 bg-muted/30 rounded-lg px-3 py-2">
                            <div>
                              <p className="text-sm font-medium text-foreground">{item?.name}</p>
                              {item?.notes && <p className="text-xs text-muted-foreground">{item?.notes}</p>}
                            </div>
                            <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium flex-shrink-0 ${item?.priority === 'high' ? 'bg-error/15 text-error' : 'bg-warning/15 text-warning'}`}>{item?.priority}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {gapAnalysis?.missingLicenses?.length > 0 && (
                    <div className="bg-card border border-border rounded-2xl p-4">
                      <h4 className="font-semibold text-sm mb-3 flex items-center gap-1.5 text-teal-400">
                        <Icon name="FileKey" size={15} />
                        Missing Licenses ({gapAnalysis?.missingLicenses?.length})
                      </h4>
                      <div className="space-y-2">
                        {gapAnalysis?.missingLicenses?.map((item, i) => (
                          <div key={i} className="flex items-start justify-between gap-2 bg-muted/30 rounded-lg px-3 py-2">
                            <div>
                              <p className="text-sm font-medium text-foreground">{item?.name}</p>
                              {item?.notes && <p className="text-xs text-muted-foreground">{item?.notes}</p>}
                            </div>
                            <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium flex-shrink-0 ${item?.priority === 'high' ? 'bg-error/15 text-error' : 'bg-warning/15 text-warning'}`}>{item?.priority}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {gapItems.length === 0 && totalGapCount === 0 && (
              <div className="text-center py-12 bg-card border border-border rounded-2xl">
                <Icon name="CheckCircle2" size={40} className="mx-auto text-success mb-3" />
                <p className="text-foreground font-medium">No gaps identified</p>
                <p className="text-sm text-muted-foreground">All requirements appear to be met</p>
              </div>
            )}
          </div>
        )}

        {/* ── RECOMMENDATIONS TAB ── */}
        {activeTab === 'recommendations' && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-card border border-border rounded-2xl p-5">
                <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                  <Icon name="Target" size={16} className="text-primary" />
                  AI Assessment
                </h3>
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="text-center bg-success/10 rounded-xl p-3">
                    <div className="text-2xl font-bold text-success">{recommendations?.readinessScore || '—'}</div>
                    <div className="text-xs text-muted-foreground">Readiness Score</div>
                  </div>
                  <div className="text-center bg-primary/10 rounded-xl p-3">
                    <div className="text-2xl font-bold text-primary">{recommendations?.probabilityOfSuccess || '—'}</div>
                    <div className="text-xs text-muted-foreground">Success Probability</div>
                  </div>
                </div>
                {recommendations?.recommendation && (
                  <div className="bg-primary/5 border border-primary/20 rounded-xl p-3">
                    <p className="text-xs font-semibold text-primary mb-1">Overall Recommendation</p>
                    <p className="text-sm text-foreground">{recommendations?.recommendation}</p>
                  </div>
                )}
              </div>
              <div className="space-y-3">
                <div className="bg-card border border-border rounded-2xl p-4">
                  <SectionList title="Missing Resources to Arrange" icon="Package" items={recommendations?.missingResources} color="text-warning" />
                </div>
                <div className="bg-card border border-border rounded-2xl p-4">
                  <SectionList title="Documents to Prepare" icon="FileText" items={recommendations?.missingDocsToPrepare} color="text-blue-400" />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-card border border-border rounded-2xl p-4">
                <SectionList title="Risks Before Submission" icon="AlertTriangle" items={recommendations?.risksBeforeSubmission} color="text-error" />
              </div>
              <div className="bg-card border border-border rounded-2xl p-4">
                <SectionList title="Suggested Actions" icon="Lightbulb" items={recommendations?.suggestedActions} color="text-success" />
              </div>
            </div>
            {!recommendations?.recommendation && (
              <div className="text-center py-8 text-muted-foreground bg-card border border-border rounded-2xl">
                <Icon name="Lightbulb" size={32} className="mx-auto mb-2 opacity-40" />
                <p className="text-sm">AI recommendations will appear after document analysis</p>
              </div>
            )}
          </div>
        )}

        {/* ── EXECUTIVE SUMMARY TAB ── */}
        {activeTab === 'summary' && (
          <div className="bg-card border border-border rounded-2xl p-6 space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="FileText" size={20} className="text-primary" />
              <h3 className="font-semibold text-foreground text-lg">Executive Summary</h3>
            </div>
            <p className="text-foreground leading-relaxed">{tender?.executiveSummary || aiAnalysis?.executiveSummary || analysis?.executiveSummary || 'No summary available.'}</p>
            {aiAnalysis?.scopeSummary && (
              <div className="bg-muted/30 rounded-xl p-4">
                <p className="text-xs font-semibold text-muted-foreground mb-1">Scope Summary</p>
                <p className="text-sm text-foreground">{aiAnalysis?.scopeSummary}</p>
              </div>
            )}
            {recommendations?.recommendation && (
              <div className="bg-primary/5 border border-primary/20 rounded-xl p-4">
                <p className="text-sm font-semibold text-primary mb-1">Recommendation</p>
                <p className="text-sm text-foreground">{recommendations?.recommendation}</p>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <SectionList title="Major Requirements" icon="ClipboardList" items={aiAnalysis?.majorRequirements} />
              </div>
              <div>
                <SectionList title="Critical Conditions" icon="AlertOctagon" items={aiAnalysis?.criticalConditions} color="text-error" />
              </div>
              <div>
                <SectionList title="Important Deadlines" icon="Calendar" items={aiAnalysis?.importantDeadlines} color="text-warning" />
              </div>
              <div>
                <SectionList title="Submission Instructions" icon="Send" items={aiAnalysis?.submissionInstructions || analysis?.submissionInstructions} color="text-blue-400" />
              </div>
              <div>
                <SectionList title="Special Notes" icon="StickyNote" items={aiAnalysis?.specialNotes} color="text-purple-400" />
              </div>
              <div>
                <SectionList title="Evaluation Criteria" icon="Star" items={aiAnalysis?.evaluationCriteria || analysis?.evaluationCriteria} color="text-success" />
              </div>
            </div>
            <div className="flex flex-wrap gap-4 pt-2">
              {(recommendations?.readinessScore || analysis?.estimatedReadiness) && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Estimated Readiness:</span>
                  <span className="text-lg font-bold text-success">{recommendations?.readinessScore || analysis?.estimatedReadiness}</span>
                </div>
              )}
              {(basicInfo?.contractDuration || aiAnalysis?.projectDuration) && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Project Duration:</span>
                  <span className="text-sm font-medium text-foreground">{basicInfo?.contractDuration || aiAnalysis?.projectDuration}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* AI Chat Drawer */}
      {chatOpen && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-black/40" onClick={() => setChatOpen(false)} />
          <div className="relative w-full max-w-md bg-card border-l border-border flex flex-col h-full shadow-2xl">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <div className="flex items-center gap-2">
                <Icon name="Brain" size={18} className="text-primary" />
                <span className="font-semibold text-foreground">AI Tender Assistant</span>
              </div>
              <button onClick={() => setChatOpen(false)} className="text-muted-foreground hover:text-foreground">
                <Icon name="X" size={18} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {chatMessages.length === 0 && (
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground text-center mb-3">Ask me anything about this tender</p>
                  {[
                    'What are the mandatory requirements?',
                    'What documents are missing?',
                    'What engineers are required?',
                    'What certifications are needed?',
                    'What are the project risks?',
                    'Summarize this tender.',
                  ].map((q) => (
                    <button key={q} onClick={() => setChatInput(q)}
                      className="w-full text-left text-sm bg-muted/40 hover:bg-muted/70 border border-border rounded-xl px-3 py-2 text-foreground transition-colors">
                      {q}
                    </button>
                  ))}
                </div>
              )}
              {chatMessages.map((msg, i) => (
                <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm ${msg.role === 'user' ? 'bg-primary text-primary-foreground' : 'bg-muted/50 text-foreground'}`}>
                    {msg.content}
                    {msg.streaming && <span className="inline-block w-1 h-3 bg-current ml-0.5 animate-pulse" />}
                  </div>
                </div>
              ))}
              <div ref={chatEndRef} />
            </div>
            <div className="p-3 border-t border-border">
              <div className="flex gap-2">
                <input
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleChatSend()}
                  placeholder="Ask about this tender..."
                  className="flex-1 bg-muted/40 border border-border rounded-xl px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                  disabled={chatLoading}
                />
                <button
                  onClick={handleChatSend}
                  disabled={chatLoading || !chatInput.trim()}
                  className="w-9 h-9 rounded-xl bg-primary text-primary-foreground flex items-center justify-center disabled:opacity-50 hover:bg-primary/90 transition-colors"
                >
                  <Icon name={chatLoading ? 'Loader2' : 'Send'} size={16} className={chatLoading ? 'animate-spin' : ''} />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default ComplianceReports;
